/**
 * StreakWidget — current adherence streak + best-ever + pause control.
 *
 * Renders the streak in beginner-friendly days OR intermediate/advanced weeks
 * depending on the user's `experience` setting. Pause button opens a calendar
 * picker — user defines start AND end dates themselves; no app-imposed limit
 * (the brand stance is: the user owns their body).
 */

import React, { useState, useMemo } from 'react';
import { Flame, Pause, Calendar, X, Award, Play } from 'lucide-react';
import { useProfile } from '../context/ProfileContext';
import { computeAdherenceStreak, currentOpenPause, isPaused, consecutiveProgramsCompleted, hasUnlockedExpertTier } from '../utils/streaks';
import { localISO } from '../data/programs';

const fmtStreak = (days, experience) => {
  if (experience === 'beginner') return { value: days, unit: days === 1 ? 'day' : 'days' };
  if (days < 14) return { value: days, unit: days === 1 ? 'day' : 'days' };
  const weeks = Math.floor(days / 7);
  return { value: weeks, unit: weeks === 1 ? 'week' : 'weeks' };
};

const StreakWidget = () => {
  const { profile, history, addStreakPause, closeStreakPause } = useProfile();
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);

  // Build the weekly schedule kinds array from the user's program. We import
  // the schedule via `profile.scheduleByWeek` or default — keeping this simple
  // by using day-pattern from history (treating non-rest days as workout days).
  // For V1 we use a permissive schedule that doesn't break streaks for missed
  // off-days: every day reads as 'rest' unless the user has trained on that
  // weekday recently. Future: derive properly from active program.
  const schedule = useMemo(() => {
    const seen = new Array(7).fill(false);
    for (const h of history || []) {
      try {
        const d = new Date((typeof h.date === 'string' ? h.date : new Date(h.date).toISOString()).slice(0, 10) + 'T00:00:00');
        seen[d.getDay()] = true;
      } catch { /* ignore */ }
    }
    return seen.map((b) => b ? 'strength' : 'rest');
  }, [history]);

  const currentStreak = useMemo(
    () => computeAdherenceStreak(history, schedule, profile.streakPauses, localISO()),
    [history, schedule, profile.streakPauses]
  );
  const todayPaused = isPaused(profile.streakPauses, localISO());
  const openPause = currentOpenPause(profile.streakPauses);
  const expertUnlocked = hasUnlockedExpertTier(profile.programCompletions);
  const programsChain = consecutiveProgramsCompleted(profile.programCompletions);

  const main = fmtStreak(currentStreak, profile.experience);
  const best = fmtStreak(profile.streakBestEver || 0, profile.experience);

  const resumeNow = () => { if (openPause) closeStreakPause(openPause.id, localISO()); };

  return (
    <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5" data-testid="streak-widget">
      <div className="flex items-center gap-2 mb-3">
        <Flame className="w-4 h-4 text-enf-accent-fg scale-x-[-1]" />
        <div className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">Streak</div>
        {expertUnlocked && (
          <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold bg-enf-accent/20 text-enf-accent-fg border border-enf-accent-fg/40">
            <Award className="w-3 h-3" /> Expert
          </span>
        )}
      </div>

      {todayPaused && (
        <div className="mb-3 p-2.5 rounded-md bg-enf-border-special/10 border border-enf-border-special/40 text-xs text-enf-border-special flex items-center gap-2" data-testid="streak-paused-banner">
          <Pause className="w-3.5 h-3.5" />
          <span>
            Streak paused
            {openPause?.end ? ` until ${openPause.end}` : ' (open-ended)'}.
          </span>
          {openPause && !openPause.end && (
            <button onClick={resumeNow} data-testid="streak-resume" className="ml-auto text-enf-border-special hover:text-enf-text-primary font-semibold underline">
              Resume now
            </button>
          )}
        </div>
      )}

      <div className="flex items-end justify-between gap-3 mb-1">
        <div>
          <div className="font-display text-5xl tracking-tight leading-none text-enf-text-primary" data-testid="streak-current-value">{main.value}</div>
          <div className="text-xs text-enf-text-muted mt-1">{main.unit} of adherence</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-2xl text-enf-role-info leading-none" data-testid="streak-best-value">{best.value}</div>
          <div className="text-[10px] uppercase tracking-widest text-enf-text-muted mt-1">Best ever</div>
        </div>
      </div>

      {programsChain > 0 && (
        <div className="mt-3 pt-3 border-t border-enf-surface-border text-xs text-enf-text-secondary flex items-center justify-between" data-testid="streak-programs-chain">
          <span>Programs completed in a row</span>
          <span className="font-mono text-enf-accent-fg font-bold">{programsChain}</span>
        </div>
      )}

      <button onClick={() => setPauseDialogOpen(true)}
        data-testid="streak-pause-button"
        className="w-full mt-3 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border hover:border-enf-border-special/40 rounded-md text-xs font-semibold uppercase tracking-wider text-enf-text-secondary hover:text-enf-border-special transition-colors flex items-center justify-center gap-1.5">
        <Pause className="w-3.5 h-3.5" /> {todayPaused ? 'Manage pause' : 'Pause streak'}
      </button>

      {pauseDialogOpen && (
        <StreakPauseDialog onClose={() => setPauseDialogOpen(false)}
          pauses={profile.streakPauses}
          onAdd={(start, end, reason, note) => { addStreakPause({ start, end, reason, note }); setPauseDialogOpen(false); }}
          onClose_={(id, end) => { closeStreakPause(id, end); setPauseDialogOpen(false); }}
        />
      )}
    </div>
  );
};

const StreakPauseDialog = ({ pauses, onAdd, onClose, onClose_ }) => {
  const today = localISO();
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  const open = (pauses || []).find((p) => !p.end);
  const [resumeDate, setResumeDate] = useState(today);

  const REASONS = ['Injured', 'Sick', 'Life', 'Recovery week', 'Travel'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6" data-testid="streak-pause-dialog">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-enf-border-special font-semibold mb-1 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> Streak pause
            </div>
            <div className="font-display text-2xl tracking-wide leading-none">You own the calendar.</div>
          </div>
          <button onClick={onClose} className="text-enf-text-muted hover:text-enf-text-primary"><X className="w-5 h-5" /></button>
        </div>

        {open ? (
          <>
            <div className="p-3 bg-enf-border-special/10 border border-enf-border-special/30 rounded-md text-sm mb-4">
              <div className="text-enf-text-secondary mb-1">Active pause</div>
              <div className="text-xs text-enf-text-muted">
                Started {open.start}
                {open.reason && <span> · {open.reason}</span>}
              </div>
            </div>
            <label className="block text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Resume date</label>
            <input type="date" value={resumeDate} min={open.start} onChange={(e) => setResumeDate(e.target.value)}
              data-testid="streak-resume-date"
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-border-special mb-4" />
            <button onClick={() => onClose_(open.id, resumeDate)}
              data-testid="streak-resume-confirm"
              className="w-full py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2">
              <Play className="w-4 h-4" /> Resume on {resumeDate}
            </button>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Start</label>
                <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                  data-testid="streak-pause-start"
                  className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-border-special" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2">End (optional)</label>
                <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)}
                  data-testid="streak-pause-end"
                  className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-border-special" />
              </div>
            </div>
            <label className="block text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Reason (optional)</label>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {REASONS.map((r) => (
                <button key={r} type="button" onClick={() => setReason(r === reason ? '' : r)}
                  data-testid={`pause-reason-${r}`}
                  className={`px-2.5 py-1 text-xs rounded border transition-colors ${reason === r ? 'bg-enf-border-special border-enf-border-special text-enf-text-primary' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border'}`}>
                  {r}
                </button>
              ))}
            </div>
            <p className="text-xs text-enf-text-muted mb-4 italic">No app-imposed limit. Set it now, or leave the end date blank and close it later.</p>
            <button onClick={() => onAdd(start, end || null, reason, '')}
              data-testid="streak-pause-confirm"
              className="w-full py-2.5 bg-enf-border-special hover:bg-enf-border-special/80 rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2">
              <Pause className="w-4 h-4" /> Start pause
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default StreakWidget;
